import {
  EventType,
  KnownMembership,
  MatrixClient,
  MatrixEvent,
  Room,
  RoomMember,
  RoomMemberEvent,
} from 'matrix-js-sdk/src/matrix';

import { getRoomStateEvents } from 'utils/roomHierarchyUtils';

export type DeferredInviteSnapshot = {
  roomId: string;
  inviterId?: string;
  inviteTimestamp: number;
  trackedAt: number;
  source: 'membership_invite' | 'membership_join_during_defer' | 'room_first_seen' | 'backend_guest';
  membershipAtTrack: string;
};

/** Finds the user's invite member event — works even after membership flips to join. */
export const findUserInviteMemberEvent = (room: Room, myUserId: string): MatrixEvent | undefined => {
  const fromState = getRoomStateEvents(room, EventType.RoomMember).find(
    (event) => event.getContent().membership === KnownMembership.Invite && event.getStateKey() === myUserId
  );
  if (fromState) return fromState;

  return room
    .getLiveTimeline()
    .getEvents()
    .find(
      (event) =>
        event.getType() === EventType.RoomMember &&
        event.getStateKey() === myUserId &&
        event.getContent().membership === KnownMembership.Invite
    );
};

export const snapshotFromInviteRoom = (room: Room, myUserId: string): DeferredInviteSnapshot | null => {
  const inviteEvent = findUserInviteMemberEvent(room, myUserId);
  if (!inviteEvent) return null;

  const inviteTimestamp = inviteEvent.getTs();
  return {
    roomId: room.roomId,
    inviterId: inviteEvent.getSender(),
    inviteTimestamp: typeof inviteTimestamp === 'number' && inviteTimestamp > 0 ? inviteTimestamp : Date.now(),
    trackedAt: Date.now(),
    source: 'membership_invite',
    membershipAtTrack: KnownMembership.Invite,
  };
};

export class DeferredInviteSnapshotStore {
  private snapshots = new Map<string, DeferredInviteSnapshot>();

  track(snapshot: DeferredInviteSnapshot): void {
    if (!this.snapshots.has(snapshot.roomId)) {
      this.snapshots.set(snapshot.roomId, snapshot);
    }
  }

  trackRoom(room: Room, myUserId: string): void {
    const snapshot = snapshotFromInviteRoom(room, myUserId);
    if (snapshot) this.track(snapshot);
  }

  drain(): DeferredInviteSnapshot[] {
    const result = [...this.snapshots.values()];
    this.snapshots.clear();
    return result;
  }

  peek(): ReadonlyMap<string, DeferredInviteSnapshot> {
    return this.snapshots;
  }

  delete(roomId: string): void {
    this.snapshots.delete(roomId);
  }
}

export const snapshotFromMembershipEvent = (event: MatrixEvent, member: RoomMember): DeferredInviteSnapshot | null => {
  if (member.membership !== KnownMembership.Invite) return null;

  const inviteTimestamp = event.getTs();
  return {
    roomId: member.roomId,
    inviterId: event.getSender(),
    inviteTimestamp: typeof inviteTimestamp === 'number' && inviteTimestamp > 0 ? inviteTimestamp : Date.now(),
    trackedAt: Date.now(),
    source: 'membership_invite',
    membershipAtTrack: KnownMembership.Invite,
  };
};

export const snapshotFromJoinDuringDefer = (event: MatrixEvent, member: RoomMember): DeferredInviteSnapshot | null => {
  if (member.membership !== KnownMembership.Join) return null;
  const ts = event.getTs();
  return {
    roomId: member.roomId,
    inviterId: event.getSender(),
    inviteTimestamp: typeof ts === 'number' && ts > 0 ? ts : Date.now(),
    trackedAt: Date.now(),
    source: 'membership_join_during_defer',
    membershipAtTrack: KnownMembership.Join,
  };
};

/** Catch-up for rooms already joined when deferral starts or before the listener attaches. */
export const snapshotFromJoinedRoom = (room: Room, myUserId: string): DeferredInviteSnapshot | null => {
  if (room.getMyMembership() !== KnownMembership.Join) return null;

  const rawMemberEvent = room.currentState?.getStateEvents(EventType.RoomMember, myUserId);
  const memberEvent = Array.isArray(rawMemberEvent) ? rawMemberEvent[0] : rawMemberEvent;
  const ts = typeof memberEvent?.getTs === 'function' ? memberEvent.getTs() : undefined;

  return {
    roomId: room.roomId,
    inviterId: typeof memberEvent?.getSender === 'function' ? memberEvent.getSender() : undefined,
    inviteTimestamp: typeof ts === 'number' && ts > 0 ? ts : Date.now(),
    trackedAt: Date.now(),
    source: 'membership_join_during_defer',
    membershipAtTrack: KnownMembership.Join,
  };
};

export const registerDeferredInviteMembershipListener = (
  matrixClient: MatrixClient,
  isDeferred: boolean,
  store: DeferredInviteSnapshotStore,
  shouldTrack: (member: RoomMember) => boolean,
  shouldTrackJoin?: (member: RoomMember) => boolean
): (() => void) => {
  const handleMembership = (event: MatrixEvent, member: RoomMember) => {
    if (!isDeferred) return;

    const myUserId = matrixClient.getUserId();
    if (!myUserId || member.userId !== myUserId) return;

    if (shouldTrack(member)) {
      const snapshot = snapshotFromMembershipEvent(event, member);
      if (snapshot) store.track(snapshot);
      return;
    }

    if (shouldTrackJoin) {
      if (!shouldTrackJoin(member)) return;
      const snapshot = snapshotFromJoinDuringDefer(event, member);
      if (snapshot) store.track(snapshot);
    }
  };

  matrixClient.on(RoomMemberEvent.Membership, handleMembership);
  return () => matrixClient.off(RoomMemberEvent.Membership, handleMembership);
};
